import { Alert, Box, Grid, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { TimeRangeFilterBar } from "../components/filters/TimeRangeFilterBar";
import { ServiceNamesFilter } from "../components/filters/ServiceNamesFilter";
import { ServiceInstancesFilter } from "../components/filters/ServiceInstancesFilter";
import { MetricNamesFilter } from "../components/filters/MetricNamesFilter";
import { FilterBar } from "../components/filters/FilterBar";
import { RefreshControls } from "../components/filters/RefreshControls";
import { Panel } from "../components/layout/Panel";
import { TimeSeriesLineChart } from "../components/charts/TimeSeriesLineChart";
import { HistogramChart } from "../components/charts/HistogramChart";
import { useTimeRangeFromQuery } from "../hooks/useRouteParams";
import {
  useServiceNamesFilterFromQuery,
  useServiceInstancesFilterFromQuery,
  useMetricNamesFilterFromQuery,
} from "../hooks/useRouteParams";
import { TimelineInteractionProvider } from "../interaction/TimelineInteractionContext";
import { useMetadata } from "../hooks/useMetadata";
import { useMetrics } from "../hooks/useMetrics";
import type { Metric } from "../api/client";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";

function buildLinesForMetric(
  metricName: string,
  metrics: Metric[],
): ReturnType<typeof TimeSeriesLineChart>["props"]["series"] {
  const byInstance = new Map<string, Metric[]>();
  for (const m of metrics) {
    const id = m.service_instance ?? "default";
    const list = byInstance.get(id) ?? [];
    list.push(m);
    byInstance.set(id, list);
  }

  const colors = ["#4f46e5", "#22c55e", "#eab308", "#ec4899", "#06b6d4", "#f97316"];

  return Array.from(byInstance.entries()).map(([instanceId, list], index) => ({
    id: instanceId,
    label: instanceId,
    color: colors[index % colors.length],
    points: list
      .map((m) => ({
        ts: new Date(m.ts),
        value: m.value,
      }))
      .sort((a, b) => a.ts.getTime() - b.ts.getTime()),
  }));
}

export function PageMetricsQuery() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { range, setRange } = useTimeRangeFromQuery();
  const { serviceNames } = useServiceNamesFilterFromQuery();
  const { serviceInstances } = useServiceInstancesFilterFromQuery();
  const { metricNames } = useMetricNamesFilterFromQuery();

  const [refreshToken, setRefreshToken] = useState(0);

  const { metadata } = useMetadata(serviceSlug);
  const { metrics, aggregation, loading, error, isFetching } = useMetrics({
    serviceSlug,
    timeRange: range,
    serviceNames:
      serviceNames.length > 0
        ? serviceNames
        : (metadata?.services.map((s) => s.service_name) ?? []),
    serviceInstances,
    metricNames,
    enabled: !!serviceSlug && (serviceNames.length > 0 || (metadata?.services.length ?? 0) > 0),
    refreshToken,
  });

  const metadataFormatted = useMemo(() => {
    if (!metadata) {
      return { services: [], instances: [], metrics: [] };
    }
    return {
      services: metadata.services.map((s) => s.service_name),
      instances: metadata.services.flatMap((s) => s.instances.map((i) => i.instance_id)),
      metrics: metadata.metrics.map((m) => m.metric_name),
    };
  }, [metadata]);

  const metricsByName = useMemo(() => {
    const byName = new Map<string, Metric[]>();
    for (const m of metrics) {
      const list = byName.get(m.metric_name) ?? [];
      list.push(m);
      byName.set(m.metric_name, list);
    }
    return byName;
  }, [metrics]);

  

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  return (
    <TimelineInteractionProvider>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
        <Panel>
          <FilterBar>
            <TimeRangeFilterBar />
            <ServiceNamesFilter options={metadataFormatted.services} />
            <ServiceInstancesFilter options={metadataFormatted.instances} />
            <MetricNamesFilter options={metadataFormatted.metrics} />
            <RefreshControls setRefreshToken={setRefreshToken} isFetching={isFetching} />
          </FilterBar>
          {error ? <PageErrorState message={error} sx={{ mt: 1.5 }} /> : null}
          {loading ? <PageLoadingState message="Loading metrics..." sx={{ mt: 1.5 }} /> : null}
        </Panel>

        <Grid container spacing={2}>
          {Array.from(metricsByName.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([metricName, metricList]) => {
            if (metricList.length === 0) return null;
            const example = metricList[0];
            if (!example) return null;
            const isHistogram =
              example.type === 2 &&
              example.histogram_data &&
              Array.isArray(example.histogram_data.bucket_counts) &&
              example.histogram_data.bucket_counts.length > 0;
            return (
              <Grid
                key={metricName}
                size={{ xs: 12, lg: 4, xl: 3 }}
                sx={{
                  "@media (min-width: 2560px)": {
                    gridColumn: "span 2", // 6 columns (12/2 = 6) on ultra-wide
                  },
                }}
              >
                <Panel title={metricName} subtitle={example.unit ?? ""} toolbar={
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary" }}>
                    {aggregation.type !== "none" && aggregation.interval ? (
                        `${aggregation.interval} ${aggregation.type}`
                    ) : (
                        "No aggregation"
                    )}
                  </Typography>
                }>
                  {isHistogram && example.histogram_data ? (
                    <HistogramChart histogram={example.histogram_data} />
                  ) : (
                    <TimeSeriesLineChart
                      series={buildLinesForMetric(metricName, metricList)}
                      yLabel={example.unit}
                      unit={example.unit}
                      xRange={range}
                      onRangeSelect={setRange}
                    />
                  )}
                </Panel>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </TimelineInteractionProvider>
  );
}
